/**
 * ProductFoamBomSpreadsheetView.tsx
 *
 * Full-screen embedded Foam / Packaging BOM spreadsheet view.
 * Columns: STT | NCC | Tên Vật Tư | KT1 | KT2 | KT3 | Loại | ĐVT | SL | Đơn giá | Thành tiền | Ghi chú
 * Real-time price calculation according to verified supplier pricing rules with VAT synchronization.
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Download,
  History,
  Lock,
  LockOpen,
  Package,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { moveRowUp, moveRowDown } from '@/components/table/ExcelEditableTable'
import type {
  FoamBomConfig,
  FoamBomLine,
  FoamBomRevision,
} from '../types/foamBomTypes'
import {
  createBlankFoamBomLine,
  compareFoamBomLines,
  getFoamAssemblyLabel,
  getFoamPackagingLabel,
  isFoamBomSpreadsheetDirty,
} from '../types/foamBomTypes'
import {
  getProductFoamBomRevisionsApi,
  createProductFoamBomRevisionApi,
  saveProductFoamBomDataApi,
  updateProductFoamBomConfigApi,
} from '@/services/boms/productFoamBomApiClient'
import { EmptyState } from '@/components/ui/EmptyState'
import { exportFoamBomToExcel, parseFoamBomExcel } from '../foamBomExcel'
import { assertWorkbookFileSize, validateWorkbookBytes } from '@/lib/safeWorkbook'
import { formatDate } from '@/lib/dateFormat'
import type { Supplier } from '@/services/suppliers'
import { supplierService } from '@/services/suppliers'
import { supplierFormatService } from '@/services/suppliers/supplierFormatService'
import { listQuotesApi, listRulesByQuoteApi } from '@/services/quotes/apiClient'
import { priceRulesToQuoteRows } from '@/features/suppliers/SupplierDetailPage'
import { resolvePriceBatchApi } from '@/services/procurement/apiClient'
import type { ResolvePricePayload, ResolvedPurchasePrice } from '@/services/procurement/types'
import type { PriceRule } from '@/services/quotes'
import {
  isCartonType,
  isThanhVType,
  isFixedType,
  GIAY_L_OPTIONS,
  getXopGocQcOptions,
  getXopLMatCatOptions,
  getDensitiesForXopTam,
  getDensitiesForXopL,
  getDensitiesForXopGoc,
} from '../packagingPriceCalc'
import { parseTsv } from '@/components/table/ExcelEditableTable'
import { Typeahead } from '@/components/forms/Typeahead'
import { materialService, type Material } from '@/services/materials'
import {
  foamQuoteFieldIsUsed,
  foamQuoteSuggestions,
  autoFillSingleFoamQuoteFields,
  pruneLaterFoamQuoteFields,
  getFoamQuoteSemantics,
  type FoamQuoteCatalogRule,
  type FoamQuoteField,
  buildFoamBomAutocompleteSuggestions,
  foamBomPriceKind,
  type FoamBomAutocompleteSuggestion,
} from '../foamBomQuoteSheet'
import {
  convertLegacyPackagingLineToStructured,
  convertStructuredPackagingToLegacy,
} from '@/services/structuredPackaging'

export function buildFoamBomBatchSignature(lines: FoamBomLine[]): string {
  const norm = (v: any) => (v === undefined || v === null ? '' : String(v).trim().toLowerCase())
  return lines
    .map((l) =>
      [
        norm(l.id),
        norm(l.item_name),
        norm(l.ncc_supplier_id),
        norm(l.type_code),
        norm(l.loai),
        norm(l.kt1),
        norm(l.kt2),
        norm(l.kt3),
        norm(l.kt4),
        norm(l.dinh_luong),
        norm(l.dvt),
        norm(l.sl),
        norm(JSON.stringify(l.params || {})),
      ].join(';'),
    )
    .join('|')
}

const DEFAULT_BOM_COLS: Record<string, number> = { stt: 3, name: 20, ncc: 11, kt1: 4, kt2: 4, kt3: 4, kt4: 4, dl: 6, uom: 4, qty: 3, price: 8, vat: 3, total: 9, note: 28, act: 5 }
const BOM_COLUMNS: Array<{ key: string; label: string; align: string; title?: string }> = [
  { key: 'stt', label: 'STT', align: 'text-center' },
  { key: 'name', label: 'Tên hàng', align: 'px-2' },
  { key: 'ncc', label: 'NCC / nguồn giá', align: 'px-2' },
  { key: 'kt1', label: 'KT1', align: 'text-center', title: 'Ý nghĩa thay đổi theo tên hàng' },
  { key: 'kt2', label: 'KT2', align: 'text-center', title: 'Ý nghĩa thay đổi theo tên hàng' },
  { key: 'kt3', label: 'KT3', align: 'text-center', title: 'Ý nghĩa thay đổi theo tên hàng' },
  { key: 'kt4', label: 'KT4', align: 'text-center', title: 'Ý nghĩa thay đổi theo tên hàng' },
  { key: 'dl', label: 'Định lượng', align: 'text-center' },
  { key: 'uom', label: 'ĐVT', align: 'text-center' },
  { key: 'qty', label: 'SL', align: 'text-center' },
  { key: 'price', label: 'Đơn giá', align: 'text-right' },
  { key: 'vat', label: 'VAT', align: 'text-right' },
  { key: 'total', label: 'Thành tiền sau VAT', align: 'text-right' },
  { key: 'note', label: 'Ghi chú', align: 'px-2' },
  { key: 'act', label: '', align: 'text-center' },
]

export function applyFoamBomAutocompleteSuggestion(
  line: FoamBomLine,
  suggestion: FoamBomAutocompleteSuggestion,
): FoamBomLine {
  const renamed = (line.item_name ?? '').trim().toLocaleLowerCase('vi') !== suggestion.name.trim().toLocaleLowerCase('vi')
  const next = { ...line, item_name: suggestion.name, loai: suggestion.name }
  if (suggestion.supplierId) next.ncc_supplier_id = suggestion.supplierId
  if (suggestion.uom) next.dvt = suggestion.uom
  // Chọn tên hàng chỉ chọn tên: KT/định lượng do người dùng chọn tiếp theo báo giá (đổi tên thì chọn lại).
  if (renamed) {
    next.kt1 = null as never
    next.kt2 = null as never
    next.kt3 = null as never
    next.kt4 = null as never
    next.dinh_luong = null as never
  }
  return next
}

export class BatchPricingCoordinator {
  private batchReqSeq = 0
  private batchSignature = ''
  private timer: ReturnType<typeof setTimeout> | null = null
  private dispatchApi: (items: any[]) => Promise<any[]>
  private onBatchResolved: (results: any[]) => void
  private onError: (err: any) => void
  private debounceMs: number

  constructor(options: {
    dispatchApi: (items: any[]) => Promise<any[]>
    onBatchResolved: (results: any[]) => void
    onError: (err: any) => void
    debounceMs?: number
  }) {
    this.dispatchApi = options.dispatchApi
    this.onBatchResolved = options.onBatchResolved
    this.onError = options.onError
    this.debounceMs = options.debounceMs ?? 300
  }

  getSeq(): number {
    return this.batchReqSeq
  }

  getSignature(): string {
    return this.batchSignature
  }

  invalidate(): number {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.batchReqSeq += 1
    this.batchSignature = ''
    return this.batchReqSeq
  }

  requestBatch(signature: string, items: any[]): { seq: number; signature: string } {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }

    this.batchReqSeq += 1
    this.batchSignature = signature
    const capturedSeq = this.batchReqSeq
    const capturedSig = signature

    this.timer = setTimeout(async () => {
      if (this.batchReqSeq !== capturedSeq || this.batchSignature !== capturedSig) {
        return
      }

      try {
        const results = await this.dispatchApi(items)
        if (this.batchReqSeq !== capturedSeq || this.batchSignature !== capturedSig) {
          return
        }
        this.onBatchResolved(results)
      } catch (err: any) {
        if (this.batchReqSeq !== capturedSeq || this.batchSignature !== capturedSig) {
          return
        }
        this.onError(err)
      }
    }, this.debounceMs)

    return { seq: capturedSeq, signature: capturedSig }
  }

  clear() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }
}

export const FOAM_PASTABLE_FIELDS: (keyof FoamBomLine)[] = [
  'loai',
  'kt1',
  'kt2',
  'kt3',
  'dinh_luong',
  'dvt',
  'sl',
  'ghi_chu',
]

export function parseFoamCell(field: keyof FoamBomLine, raw: string): any {
  const trimmed = raw.trim()
  switch (field) {
    case 'loai':
    case 'dvt':
    case 'ghi_chu':
      return trimmed
    case 'kt1':
    case 'kt2': {
      if (trimmed === '') return null
      const n = Number(trimmed)
      return isNaN(n) ? trimmed : n
    }
    case 'kt3': {
      if (trimmed === '') return null
      const n = Number(trimmed)
      return isNaN(n) ? null : n
    }
    case 'dinh_luong': {
      if (trimmed === '') return null
      const n = Number(trimmed)
      return isNaN(n) ? trimmed : n
    }
    case 'sl': {
      const n = Number(trimmed)
      return isNaN(n) || n <= 0 ? 1 : n
    }
    default:
      return trimmed
  }
}

export function applyFoamBomTsv(
  tsv: string,
  currentLines: FoamBomLine[],
  startRow: number = 0,
  startField: keyof FoamBomLine = 'loai',
): FoamBomLine[] {
  const parsedRows = parseTsv(tsv)
  if (parsedRows.length === 0) return currentLines

  const next = currentLines.map((l) => ({ ...l }))
  const targetRowCount = startRow + parsedRows.length
  while (next.length < targetRowCount) {
    next.push(createBlankFoamBomLine())
  }

  const startColIdx = FOAM_PASTABLE_FIELDS.indexOf(startField)
  const actualStartCol = startColIdx >= 0 ? startColIdx : 0

  const firstRowCols = parsedRows[0].length
  // Detect if 7 columns pasted at 'loai' without ĐVT: [loai, kt1, kt2, kt3, dinh_luong, sl, ghi_chu]
  const isSevenColWithoutDvt =
    actualStartCol === 0 &&
    firstRowCols === 7 &&
    !isNaN(Number(parsedRows[0][5]?.trim())) &&
    parsedRows[0][5]?.trim() !== ''

  // Detect if full table with STT was copied (col 0 is number or starts with STT)
  const hasSttCol =
    actualStartCol === 0 &&
    firstRowCols >= 8 &&
    (!isNaN(Number(parsedRows[0][0]?.trim())) || /^stt/i.test(parsedRows[0][0]?.trim()))

  parsedRows.forEach((rowCells, rOff) => {
    const rIndex = startRow + rOff
    if (rIndex >= next.length) return
    const rowObj = { ...next[rIndex] }

    let cellsToUse = rowCells
    if (hasSttCol) {
      cellsToUse = rowCells.slice(1)
    }

    let fieldsMapping: (keyof FoamBomLine)[]
    if (isSevenColWithoutDvt) {
      fieldsMapping = ['loai', 'kt1', 'kt2', 'kt3', 'dinh_luong', 'sl', 'ghi_chu']
    } else {
      fieldsMapping = FOAM_PASTABLE_FIELDS.slice(actualStartCol)
    }

    cellsToUse.forEach((cellRaw, cOff) => {
      if (cOff >= fieldsMapping.length) return
      const field = fieldsMapping[cOff]
      ;(rowObj as Record<string, any>)[field] = parseFoamCell(field, cellRaw)
    })

    // Auto-adjust special materials if loai was pasted
    const newLoai = rowObj.loai || ''
    if (isCartonType(newLoai) && !rowObj.dinh_luong) {
      rowObj.dinh_luong = 'Thùng A1'
    } else if ((newLoai === 'Giấy L 5 lớp' || newLoai === 'Giấy L 3 lớp') && !rowObj.kt2) {
      rowObj.kt2 = '80x80'
    } else if (isThanhVType(newLoai) && !rowObj.kt2) {
      if (newLoai.includes('3ly') || newLoai.includes('3 ly')) {
        rowObj.kt2 = '50*50*3'
      } else if (newLoai.includes('5ly') || newLoai.includes('5 ly')) {
        rowObj.kt2 = '50*50*5'
      }
    } else if (newLoai === 'GÓC V CỨNG' && !rowObj.kt1) {
      rowObj.kt1 = '50*50*5'
    }

    next[rIndex] = rowObj
  })

  return next
}

/**
 * Pure transition function for material type ("Loại") changes on a FoamBomLine.
 * Enforces field resets (e.g. carton dims reset to null) and auto-defaults for each material type.
 */
export function applyLoaiChangeToLine(
  line: FoamBomLine,
  newLoai: string,
  activeQuoteRows: Record<string, any>[] = [],
): FoamBomLine {
  const isCarton = isCartonType(newLoai)
  const isThanhV = isThanhVType(newLoai)
  const isFixed = isFixedType(newLoai)

  const cur = { ...line, loai: newLoai }

  if (newLoai === 'Xốp' || newLoai === 'Xốp tấm') {
    cur.dvt = 'm³'
    if (typeof cur.kt1 !== 'number') cur.kt1 = null
    if (typeof cur.kt2 !== 'number') cur.kt2 = null
    if (typeof cur.kt3 !== 'number') cur.kt3 = null
    const validDens = getDensitiesForXopTam(activeQuoteRows)
    const curDens = formatDensityDisplay(cur.dinh_luong)
    if (!cur.dinh_luong || !validDens.includes(curDens)) {
      cur.dinh_luong = validDens.length === 1 ? validDens[0] : null
    }
  } else if (newLoai === 'Xốp góc') {
    cur.dvt = 'cái'
    cur.kt2 = null
    cur.kt3 = null
    const qcOpts = getXopGocQcOptions(activeQuoteRows)
    if (!cur.kt1 || !qcOpts.includes(String(cur.kt1))) {
      cur.kt1 = qcOpts.length === 1 ? qcOpts[0] : null
    }
    const validDens = getDensitiesForXopGoc(String(cur.kt1), activeQuoteRows)
    const curDens = formatDensityDisplay(cur.dinh_luong)
    if (!cur.dinh_luong || !validDens.includes(curDens)) {
      cur.dinh_luong = validDens.length === 1 ? validDens[0] : null
    }
  } else if (newLoai === 'Xốp L') {
    cur.dvt = 'mét'
    cur.kt3 = null
    if (typeof cur.kt1 !== 'number') cur.kt1 = null
    const mcOpts = getXopLMatCatOptions(activeQuoteRows)
    if (!cur.kt2 || !mcOpts.includes(String(cur.kt2))) {
      cur.kt2 = mcOpts.length === 1 ? mcOpts[0] : null
    }
    const validDens = getDensitiesForXopL(String(cur.kt2), activeQuoteRows)
    const curDens = formatDensityDisplay(cur.dinh_luong)
    if (!cur.dinh_luong || !validDens.includes(curDens)) {
      cur.dinh_luong = validDens.length === 1 ? validDens[0] : null
    }
  } else if (isCarton) {
    cur.kt1 = null
    cur.kt2 = null
    cur.kt3 = null
    cur.dvt = 'thùng'
    if (!cur.dinh_luong || !['Thùng A1', 'Thùng nắp chôm'].includes(String(cur.dinh_luong))) {
      cur.dinh_luong = 'Thùng A1'
    }
  } else if (newLoai === 'Giấy L 5 lớp' || newLoai === 'Giấy L 3 lớp') {
    cur.dvt = 'm²'
    cur.kt3 = null
    if (typeof cur.kt1 !== 'number') cur.kt1 = null
    const validGiayLOpts = GIAY_L_OPTIONS.map((o) => o.label as string)
    if (!validGiayLOpts.includes(String(cur.kt2))) {
      cur.kt2 = validGiayLOpts.length === 1 ? validGiayLOpts[0] : null
    }
    cur.dinh_luong = null
  } else if (newLoai === 'Giấy tấm 5L' || newLoai === 'Giấy tấm 3 lớp') {
    cur.dvt = 'm²'
    cur.kt3 = null
    if (typeof cur.kt1 !== 'number') cur.kt1 = null
    if (typeof cur.kt2 !== 'number') cur.kt2 = null
    cur.dinh_luong = null
  } else if (isThanhV) {
    cur.dvt = 'mét'
    cur.kt3 = null
    if (typeof cur.kt1 !== 'number') cur.kt1 = null
    cur.dinh_luong = null
    if (newLoai.includes('3ly') || newLoai.includes('3 ly')) {
      cur.kt2 = '50*50*3'
    } else if (newLoai.includes('5ly') || newLoai.includes('5 ly')) {
      cur.kt2 = '50*50*5'
    } else {
      cur.kt2 = null
    }
  } else if (newLoai === 'Góc giấy 5 lớp') {
    cur.dvt = 'cái'
    if (typeof cur.kt1 !== 'number' && cur.kt1 !== '80x80x80' && cur.kt1 !== '100x100x100') {
      cur.kt1 = 80
      cur.kt2 = 80
      cur.kt3 = 80
    }
    cur.dinh_luong = null
  } else if (newLoai === 'GÓC V CỨNG') {
    cur.dvt = 'cái'
    cur.kt1 = '50*50*5'
    cur.kt2 = null
    cur.kt3 = null
    cur.dinh_luong = null
  } else if (isFixed) {
    cur.dvt = 'cái'
    cur.kt1 = null
    cur.kt2 = null
    cur.kt3 = null
    cur.dinh_luong = null
  }

  return cur
}

export function moveFoamBomLine(
  lines: FoamBomLine[],
  index: number,
  direction: 'up' | 'down',
): FoamBomLine[] {
  return direction === 'up' ? moveRowUp(lines, index) : moveRowDown(lines, index)
}

export function reconcileLinesWithQuoteRules(
  lines: FoamBomLine[],
  supplierId: string,
  rows: Record<string, any>[],
): { nextLines: FoamBomLine[]; hasChange: boolean } {
  let hasChange = false
  const nextLines = lines.map((line) => {
    if (line.ncc_supplier_id !== supplierId) return line
    const updatedLine = { ...line }

    if (updatedLine.loai === 'Xốp góc') {
      const qcOpts = getXopGocQcOptions(rows)
      if (!updatedLine.kt1 || !qcOpts.includes(String(updatedLine.kt1))) {
        const nextKt1 = qcOpts.length === 1 ? qcOpts[0] : null
        if (updatedLine.kt1 !== nextKt1) {
          updatedLine.kt1 = nextKt1
          hasChange = true
        }
      }
      const validDens = getDensitiesForXopGoc(String(updatedLine.kt1), rows)
      const curDens = formatDensityDisplay(updatedLine.dinh_luong)
      if (!updatedLine.dinh_luong || !validDens.includes(curDens)) {
        const nextDens = validDens.length === 1 ? validDens[0] : null
        if (updatedLine.dinh_luong !== nextDens) {
          updatedLine.dinh_luong = nextDens
          hasChange = true
        }
      }
    } else if (updatedLine.loai === 'Xốp L') {
      const mcOpts = getXopLMatCatOptions(rows)
      if (!updatedLine.kt2 || !mcOpts.includes(String(updatedLine.kt2))) {
        const nextKt2 = mcOpts.length === 1 ? mcOpts[0] : null
        if (updatedLine.kt2 !== nextKt2) {
          updatedLine.kt2 = nextKt2
          hasChange = true
        }
      }
      const validDens = getDensitiesForXopL(String(updatedLine.kt2), rows)
      const curDens = formatDensityDisplay(updatedLine.dinh_luong)
      if (!updatedLine.dinh_luong || !validDens.includes(curDens)) {
        const nextDens = validDens.length === 1 ? validDens[0] : null
        if (updatedLine.dinh_luong !== nextDens) {
          updatedLine.dinh_luong = nextDens
          hasChange = true
        }
      }
    } else if (updatedLine.loai === 'Xốp' || updatedLine.loai === 'Xốp tấm') {
      const validDens = getDensitiesForXopTam(rows)
      const curDens = formatDensityDisplay(updatedLine.dinh_luong)
      if (!updatedLine.dinh_luong || !validDens.includes(curDens)) {
        const nextDens = validDens.length === 1 ? validDens[0] : null
        if (updatedLine.dinh_luong !== nextDens) {
          updatedLine.dinh_luong = nextDens
          hasChange = true
        }
      }
    }
    return updatedLine
  })
  return { nextLines, hasChange }
}

interface ProductFoamBomSpreadsheetViewProps {
  productId: string
  productCode: string
  productName: string
  productImageUrl?: string | null
  customer?: string
  config: FoamBomConfig
  initialLines: FoamBomLine[]
  initialEditMode?: boolean
  onBack: () => void
}

function DataRouterBlocker({ when }: { when: boolean }) {
  const blocker = useBlocker(when)

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const confirmLeave = window.confirm(
        'Bạn có thay đổi chưa lưu, chắc chắn muốn rời trang?',
      )
      if (confirmLeave) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  return null
}

function SafeBlocker({ when }: { when: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  if (!dataRouter) return null
  return <DataRouterBlocker when={when} />
}

const DEFAULT_DINH_LUONG_OPTIONS = ['8kg', '10kg', '12kg', '14kg', '16kg', '18kg', '20kg', '22kg', '24kg']
const DEFAULT_DVT_OPTIONS = ['Cái', 'Tấm', 'Bộ', 'Kg', 'Cuộn', 'Mét', 'Hộp', 'Túi', 'm³', 'thùng', 'm²']

export function getPackagingSupplierCategory(
  s: Supplier | null | undefined,
): 'Bao Bì' | 'Mút Xốp' | string {
  if (!s) return '—'
  const fmt = supplierFormatService.getSupplierFormat(s.id, s.name)
  const nameLower = (s.name || '').toLowerCase()
  if (
    fmt === 'mut_xop' ||
    fmt.includes('xop') ||
    nameLower.includes('mút') ||
    nameLower.includes('xốp') ||
    nameLower.includes('foam') ||
    nameLower.includes('nhân phát')
  ) {
    return 'Mút Xốp'
  }
  if (
    fmt === 'carton' ||
    fmt.includes('bao_bi') ||
    fmt.includes('bb') ||
    nameLower.includes('bao bì') ||
    nameLower.includes('carton') ||
    nameLower.includes('thịnh phát')
  ) {
    return 'Bao Bì'
  }
  return s.name
}

export function formatDensityDisplay(val: unknown): string {
  if (val == null || val === '') return '—'
  const str = String(val).trim()
  if (str.toLowerCase().endsWith('kg')) return str
  const n = Number(str)
  if (!isNaN(n) && n > 0) return `${n}kg`
  return str
}

export function formatPricingWarningText(reason?: string | null): string {
  if (!reason) return ''
  const lower = reason.toLowerCase()
  if (lower.includes('nhiều dòng báo giá') || lower.includes('chọn một dòng báo giá')) return 'Nhiều báo giá'
  if (lower.includes('không tìm thấy')) return 'Chưa có giá'
  if (lower.includes('vui lòng chọn')) return 'Cần chọn giá'
  return reason
}

export function ProductFoamBomSpreadsheetView({
  productId,
  productCode,
  productName,
  productImageUrl,
  customer = 'Williams Sonoma / Crate & Barrel',
  config,
  initialLines,
  initialEditMode = false,
  onBack,
}: ProductFoamBomSpreadsheetViewProps) {
  const [lines, setLines] = useState<FoamBomLine[]>(initialLines)
  const linesRef = useRef<FoamBomLine[]>(initialLines)
  linesRef.current = lines

  // Server-resolved price results for each row
  const [resolvedPrices, setResolvedPrices] = useState<Record<string, ResolvedPurchasePrice>>({})
  const [pricingError, setPricingError] = useState<string | null>(null)
  /** Đang chờ giá mới từ máy chủ: giữ giá cũ nhưng làm mờ, không xóa trắng (tránh bảng nhấp nháy khi gõ). */
  const [pricingPending, setPricingPending] = useState(false)
  const [showSupplierColumn, setShowSupplierColumn] = useState(false)
  /** Độ rộng cột (%): cột chữ rộng, cột số nhỏ; kéo mép tiêu đề để chỉnh, được nhớ trên máy. */
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem('foam-bom-col-widths') || '{}') as Record<string, number>
      return { ...DEFAULT_BOM_COLS, ...saved }
    } catch {
      return { ...DEFAULT_BOM_COLS }
    }
  })
  const visibleCols = BOM_COLUMNS.filter((c) => c.key !== 'ncc' || showSupplierColumn)
  const totalWeight = visibleCols.reduce((sum, c) => sum + (colWidths[c.key] || DEFAULT_BOM_COLS[c.key]), 0)
  const startColumnResize = (event: React.MouseEvent, colIndex: number) => {
    event.preventDefault()
    const tableWidth = (event.currentTarget as HTMLElement).closest('table')?.getBoundingClientRect().width || 1
    const left = visibleCols[colIndex].key
    const right = visibleCols[colIndex + 1].key
    const startX = event.clientX
    const start = { ...colWidths }
    const move = (e: MouseEvent) => {
      const delta = ((e.clientX - startX) / tableWidth) * totalWeight
      const l = Math.max(2, start[left] + delta)
      const r = Math.max(2, start[right] - (l - start[left]))
      setColWidths({ ...start, [left]: l, [right]: r })
    }
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      setColWidths((cur) => {
        try { window.localStorage.setItem('foam-bom-col-widths', JSON.stringify(cur)) } catch { /* bỏ qua */ }
        return cur
      })
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  const coordinatorRef = useRef<BatchPricingCoordinator | null>(null)
  if (!coordinatorRef.current) {
    coordinatorRef.current = new BatchPricingCoordinator({
      dispatchApi: (items) => resolvePriceBatchApi(items),
      onBatchResolved: (results) => {
        const priceMap: Record<string, ResolvedPurchasePrice> = {}
        linesRef.current.forEach((line, idx) => {
          const key = line.id || String(idx)
          if (results[idx]) {
            priceMap[key] = results[idx]
          }
        })
        setResolvedPrices(priceMap)
        setPricingError(null)
        setPricingPending(false)
      },
      onError: (err) => {
        console.error('Lỗi tính giá BOM đóng gói từ máy chủ:', err)
        setResolvedPrices({})
        setPricingPending(false)
        setPricingError(err?.message || 'Không thể tính giá từ máy chủ. Vui lòng thử lại.')
      },
      debounceMs: 300,
    })
  }

  const updateLines = useCallback(
    (newLinesOrUpdater: FoamBomLine[] | ((prev: FoamBomLine[]) => FoamBomLine[])) => {
      // Invariant:
      // INPUT CHANGE
      // → invalidate request cũ NGAY
      // → clear stale authoritative price
      // → schedule request mới
      coordinatorRef.current?.invalidate()
      setPricingPending(true)
      setPricingError(null)

      setLines((prev) => {
        const nextLines =
          typeof newLinesOrUpdater === 'function' ? newLinesOrUpdater(prev) : newLinesOrUpdater
        linesRef.current = nextLines
        const sig = buildFoamBomBatchSignature(nextLines)
        const items: ResolvePricePayload[] = nextLines.map((line) => ({
          supplierId: line.ncc_supplier_id || null,
          packagingKind: foamBomPriceKind(line),
          kt1: line.kt1,
          kt2: line.kt2,
          kt3: line.kt3,
          kt4: line.kt4,
          density: line.dinh_luong,
          qty: line.sl || 1,
          allowEstimate: true,
        }))
        coordinatorRef.current?.requestBatch(sig, items)
        return nextLines
      })
    },
    [],
  )

  useEffect(() => {
    const sig = buildFoamBomBatchSignature(initialLines)
    const items: ResolvePricePayload[] = initialLines.map((line) => ({
      supplierId: line.ncc_supplier_id || null,
      packagingKind: foamBomPriceKind(line),
      kt1: line.kt1,
      kt2: line.kt2,
      kt3: line.kt3,
      kt4: line.kt4,
      density: line.dinh_luong,
      qty: line.sl || 1,
      allowEstimate: true,
    }))
    coordinatorRef.current?.requestBatch(sig, items)

    return () => {
      coordinatorRef.current?.clear()
    }
  }, [])

  const [currentConfig, setCurrentConfig] = useState<FoamBomConfig>(config)
  const [isEditMode, setIsEditMode] = useState(initialEditMode)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [revisions, setRevisions] = useState<FoamBomRevision[]>([])
  const [currentRev, setCurrentRev] = useState<FoamBomRevision | null>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)

  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [allQuoteCatalog, setFoamQuoteCatalog] = useState<FoamQuoteCatalogRule[]>([])
  const [packagingMaterials, setPackagingMaterials] = useState<Material[]>([])
  const [supplierQuotesMap, setSupplierQuotesMap] = useState<
    Record<string, Record<string, unknown>[]>
  >({})

  const editBaselineRef = useRef<FoamBomLine[] | null>(null)
  const configBaselineRef = useRef<FoamBomConfig | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const focusedCellRef = useRef<{ rowIndex: number; field: keyof FoamBomLine } | null>(null)

  function showToast(msg: string) {
    setToastMsg(msg)
    setTimeout(() => {
      setToastMsg((cur) => (cur === msg ? null : cur))
    }, 3000)
  }

  // Load suppliers on mount
  useEffect(() => {
    let active = true
    supplierService
      .list()
      .then((list) => {
        if (!active) return
        setSuppliers(list)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [])

  // BOM đóng gói chỉ dùng vật tư ánh xạ trong báo giá NCC bao bì / mút xốp: loại các dòng báo giá của ngũ kim.
  const foamQuoteCatalog = useMemo(() => {
    const hardwareIds = new Set(packagingMaterials.filter((m) => m.group_code === 'nk').map((m) => m.id))
    return allQuoteCatalog.filter((rule) => !rule.itemId || !hardwareIds.has(rule.itemId))
  }, [allQuoteCatalog, packagingMaterials])

  useEffect(() => {
    let active = true
    void materialService.list().then((materials) => {
      if (!active) return
      setPackagingMaterials(materials)
    }).catch(() => {
      if (!active) return
      setPackagingMaterials([])
    })
    return () => { active = false }
  }, [])

  useEffect(() => {
    let active = true
    // Mọi NCC đang hoạt động có báo giá dạng bảng: không giới hạn theo danh mục (NCC xốp/carton có thể nằm ở danh mục khác).
    const quoteSuppliers = suppliers.filter((supplier) => supplier.status === 'ACTIVE')
    if (quoteSuppliers.length === 0) {
      setFoamQuoteCatalog([])
      return () => { active = false }
    }
    const today = new Date().toISOString().slice(0, 10)
    void Promise.all(quoteSuppliers.map(async (supplier) => {
      const quotes = await listQuotesApi({ supplier_id: supplier.id })
      const activeQuotes = quotes.filter((quote) => quote.status === 'ACTIVE' &&
        (!quote.effective_from || quote.effective_from <= today) &&
        (!quote.effective_until || quote.effective_until >= today))
      const quoteRules = await Promise.all(activeQuotes.map((quote) => listRulesByQuoteApi(quote.id)))
      return quoteRules.flatMap((rules) => rules.flatMap((rule) => {
        const sheet = rule.spec_attributes?.sheet as { mappings?: Record<string, unknown>; input_names?: string[] } | undefined
        if (!sheet || rule.status !== 'ACTIVE') return []
        return [{
          supplierId: supplier.id,
          supplierName: supplier.name,
          itemName: rule.supplier_item_name,
          mappings: sheet.mappings || {},
          inputNames: sheet.input_names || [],
          uom: rule.uom,
          itemId: rule.item_id ?? null,
        }]
      }))
    })).then((catalogs) => {
      if (active) setFoamQuoteCatalog(catalogs.flat())
    }).catch((error) => {
      console.error('Lỗi tải danh mục báo giá BOM bao bì:', error)
      if (active) setFoamQuoteCatalog([])
    })
    return () => { active = false }
  }, [suppliers])

  const usedPackagingNames = useMemo(
    () => lines.flatMap((line) => [line.item_name, line.loai]).filter((name): name is string => Boolean(name)),
    [lines],
  )

  // Supplier map by id
  const supplierMap = useMemo(() => {
    const map = new Map<string, Supplier>()
    suppliers.forEach((s) => map.set(s.id, s))
    return map
  }, [suppliers])

  // Helper to load quote rules for a supplier
  const loadQuoteRulesForSupplier = useCallback(
    async (supplierId: string) => {
      if (!supplierId || supplierQuotesMap[supplierId]) return
      try {
        const quotes = await listQuotesApi({ supplier_id: supplierId })
        const today = new Date().toISOString().slice(0, 10)
        const activeQuotes = quotes.filter(
          (q) =>
            q.status === 'ACTIVE' &&
            (!q.effective_from || q.effective_from <= today) &&
            (!q.effective_until || q.effective_until >= today),
        )
        if (activeQuotes.length === 0) {
          setSupplierQuotesMap((prev) => ({ ...prev, [supplierId]: [] }))
          return
        }

        const allRules: PriceRule[] = []
        for (const q of activeQuotes) {
          const r = await listRulesByQuoteApi(q.id)
          allRules.push(...r)
        }

        await supplierFormatService.fetchFormats(supplierId)
        const allFormats = supplierFormatService.getAll()
        const fmtId = supplierFormatService.getSupplierFormat(
          supplierId,
          supplierMap.get(supplierId)?.name || '',
        )
        const fmt =
          supplierFormatService.getById(fmtId) ||
          allFormats.find((f) => f.id === fmtId)

        const rows = priceRulesToQuoteRows(allRules, fmt, fmtId)
        setSupplierQuotesMap((prev) => ({ ...prev, [supplierId]: rows }))

        // Update lines waiting for this supplier's quote data
        updateLines((prevLines) => {
          const { nextLines, hasChange } = reconcileLinesWithQuoteRules(prevLines, supplierId, rows)
          return hasChange ? nextLines : prevLines
        })
      } catch (err: any) {
        console.error('Lỗi tải báo giá nhà cung cấp:', err)
        setPricingError(err.message || 'Lỗi tải báo giá nhà cung cấp')
      }
    },
    [supplierQuotesMap, supplierMap],
  )

  // Load quote rules for all suppliers already in lines
  useEffect(() => {
    lines.forEach((l) => {
      if (l.ncc_supplier_id) {
        loadQuoteRulesForSupplier(l.ncc_supplier_id)
      }
    })
  }, [lines, loadQuoteRulesForSupplier])

  // Load revisions on mount
  useEffect(() => {
    let active = true
    getProductFoamBomRevisionsApi(productId, config.id)
      .then((revs) => {
        if (!active) return
        setRevisions(revs)
        if (revs.length > 0) {
          setCurrentRev(revs[0])
        }
      })
      .catch((err) => {
        console.error('Lỗi tải lịch sử phiên bản BOM:', err)
      })
    return () => {
      active = false
    }
  }, [productId, config.id])

  // Track unsaved changes
  const hasLineChanges = useMemo(
    () => isFoamBomSpreadsheetDirty(lines, editBaselineRef.current, isEditMode),
    [lines, isEditMode],
  )
  const hasConfigChanges = useMemo(() => {
    if (!isEditMode || !configBaselineRef.current) return false
    const base = configBaselineRef.current
    return (
      (currentConfig.phu_bi_dai ?? null) !== (base.phu_bi_dai ?? null) ||
      (currentConfig.phu_bi_rong ?? null) !== (base.phu_bi_rong ?? null) ||
      (currentConfig.phu_bi_cao ?? null) !== (base.phu_bi_cao ?? null) ||
      (currentConfig.net_weight ?? null) !== (base.net_weight ?? null) ||
      (currentConfig.gross_weight ?? null) !== (base.gross_weight ?? null) ||
      (currentConfig.phu_bi_2_dai ?? null) !== (base.phu_bi_2_dai ?? null) ||
      (currentConfig.phu_bi_2_rong ?? null) !== (base.phu_bi_2_rong ?? null) ||
      (currentConfig.phu_bi_2_cao ?? null) !== (base.phu_bi_2_cao ?? null) ||
      (currentConfig.net_weight_2 ?? null) !== (base.net_weight_2 ?? null) ||
      (currentConfig.gross_weight_2 ?? null) !== (base.gross_weight_2 ?? null) ||
      (currentConfig.phu_bi_3_dai ?? null) !== (base.phu_bi_3_dai ?? null) ||
      (currentConfig.phu_bi_3_rong ?? null) !== (base.phu_bi_3_rong ?? null) ||
      (currentConfig.phu_bi_3_cao ?? null) !== (base.phu_bi_3_cao ?? null) ||
      (currentConfig.net_weight_3 ?? null) !== (base.net_weight_3 ?? null) ||
      (currentConfig.gross_weight_3 ?? null) !== (base.gross_weight_3 ?? null)
    )
  }, [currentConfig, isEditMode])

  const hasUnsavedChanges = hasLineChanges || hasConfigChanges

  // beforeunload protection
  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  // Handle back navigation
  function handleBack() {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        'Bạn có thay đổi chưa lưu, chắc chắn muốn rời trang?',
      )
      if (!confirmLeave) return
    }
    onBack()
  }

  // Toggle edit mode / save
  async function handleToggleEdit() {
    if (!isEditMode) {
      editBaselineRef.current = JSON.parse(JSON.stringify(lines))
      configBaselineRef.current = JSON.parse(JSON.stringify(currentConfig))
      setIsEditMode(true)
      showToast('✏️ Đã mở chế độ chỉnh sửa! Bạn có thể sửa ô hoặc nạp Excel.')
    } else {
      // New rows use the quote-sheet shape. Rows not converted yet remain byte-for-byte legacy.
      const enrichedLines = lines.map((line) => {
        if (line.item_name !== undefined) {
          return {
            id: line.id,
            sl: line.sl,
            dvt: 'Pcs',
            item_name: line.item_name,
            ncc_supplier_id: line.ncc_supplier_id,
            kt1: line.kt1 ?? null,
            kt2: line.kt2 ?? null,
            kt3: line.kt3 ?? null,
            kt4: line.kt4 ?? null,
            dinh_luong: line.dinh_luong ?? null,
            ghi_chu: line.ghi_chu || '',
          } as FoamBomLine
        }
        const conv = convertLegacyPackagingLineToStructured(line)
        const legacy = convertStructuredPackagingToLegacy(conv.type_code, conv.params)
        return {
          ...line,
          type_code: conv.type_code,
          params: conv.params,
          loai: legacy.loai,
          kt1: legacy.kt1,
          kt2: legacy.kt2,
          kt3: legacy.kt3,
          dinh_luong: legacy.dinh_luong,
          dvt: legacy.dvt,
        }
      })

      // Save data
      try {
        await Promise.all([
          saveProductFoamBomDataApi(productId, config.id, enrichedLines),
          updateProductFoamBomConfigApi(productId, config.id, {
            phu_bi_dai: currentConfig.phu_bi_dai ?? null,
            phu_bi_rong: currentConfig.phu_bi_rong ?? null,
            phu_bi_cao: currentConfig.phu_bi_cao ?? null,
            net_weight: currentConfig.net_weight ?? null,
            gross_weight: currentConfig.gross_weight ?? null,
            phu_bi_2_dai: currentConfig.phu_bi_2_dai ?? null,
            phu_bi_2_rong: currentConfig.phu_bi_2_rong ?? null,
            phu_bi_2_cao: currentConfig.phu_bi_2_cao ?? null,
            net_weight_2: currentConfig.net_weight_2 ?? null,
            gross_weight_2: currentConfig.gross_weight_2 ?? null,
            phu_bi_3_dai: currentConfig.phu_bi_3_dai ?? null,
            phu_bi_3_rong: currentConfig.phu_bi_3_rong ?? null,
            phu_bi_3_cao: currentConfig.phu_bi_3_cao ?? null,
            net_weight_3: currentConfig.net_weight_3 ?? null,
            gross_weight_3: currentConfig.gross_weight_3 ?? null,
          }),
        ])
        setLines(enrichedLines)
      } catch (err: any) {
        showToast(`❌ Lỗi lưu dữ liệu: ${err.message || 'Lỗi hệ thống'}`)
        return
      }

      const baseline = editBaselineRef.current || []
      const diffs = compareFoamBomLines(baseline, lines)

      if (diffs.length > 0) {
        const nextRevNum = (currentRev?.revNumber || revisions.length || 0) + 1
        const nextRevLabel = `Rev. ${nextRevNum < 10 ? '0' + nextRevNum : nextRevNum}`
        const todayStr = formatDate(new Date())
        const now = new Date()
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

        const addedCount = diffs.filter((d) => d.type === 'row_added').length
        const deletedCount = diffs.filter((d) => d.type === 'row_deleted').length
        const editCount = diffs.filter((d) => d.type === 'cell_change').length
        const summaryParts: string[] = []
        if (addedCount > 0) summaryParts.push(`Thêm ${addedCount} dòng`)
        if (deletedCount > 0) summaryParts.push(`Xóa ${deletedCount} dòng`)
        if (editCount > 0) summaryParts.push(`Sửa ${editCount} ô`)

        const newRev: FoamBomRevision = {
          id: `rev-${Date.now()}`,
          revNumber: nextRevNum,
          revLabel: nextRevLabel,
          savedAt: todayStr,
          savedTime: timeStr,
          author: 'Admin',
          changesSummary: summaryParts.join(', ') || `${diffs.length} thay đổi`,
          diffs,
          snapshot: {
            lines: JSON.parse(JSON.stringify(lines)),
          },
        }

        try {
          await createProductFoamBomRevisionApi(productId, config.id, newRev)
          setRevisions((prev) => [newRev, ...prev])
          setCurrentRev(newRev)
        } catch (err) {
          console.error('Lỗi tạo phiên bản lịch sử BOM:', err)
        }
        showToast(`💾 ✓ Đã lưu BOM Đóng Gói! Phiên bản: ${nextRevLabel}`)
      } else {
        showToast('💾 ✓ Đã lưu dữ liệu bảng BOM Đóng Gói thành công!')
      }

      editBaselineRef.current = null
      configBaselineRef.current = null
      setIsEditMode(false)
    }
  }

  // Cancel edits
  function handleCancelEdit() {
    if (editBaselineRef.current) {
      updateLines(editBaselineRef.current)
    }
    if (configBaselineRef.current) {
      setCurrentConfig(configBaselineRef.current)
    }
    editBaselineRef.current = null
    configBaselineRef.current = null
    setIsEditMode(false)
    showToast('Đã hủy các thay đổi chưa lưu.')
  }

  // Row operations
  function handleAddRow() {
    if (!isEditMode) {
      editBaselineRef.current = JSON.parse(JSON.stringify(lines))
      configBaselineRef.current = JSON.parse(JSON.stringify(currentConfig))
      setIsEditMode(true)
    }
    updateLines((prev) => [...prev, createBlankFoamBomLine()])
  }

  function handleDeleteRow(index: number) {
    if (!isEditMode) {
      showToast('⚠️ Vui lòng bấm "✏️ Chỉnh sửa" trước khi xóa dòng!')
      return
    }
    updateLines((prev) => prev.filter((_, i) => i !== index))
  }

  function handleMoveRowUp(index: number) {
    if (!isEditMode) return
    updateLines((prev) => moveRowUp(prev, index))
  }

  function handleMoveRowDown(index: number) {
    if (!isEditMode) return
    updateLines((prev) => moveRowDown(prev, index))
  }

  function handleCellChange<K extends keyof FoamBomLine>(
    index: number,
    field: K,
    value: FoamBomLine[K],
  ) {
    if (!isEditMode) return
    updateLines((prev) => {
      const updated = [...prev]
      const cur = { ...updated[index], [field]: value }
      if (cur.item_name !== undefined) {
        cur.dvt = 'Pcs'
      } else {
        const conv = convertLegacyPackagingLineToStructured(cur)
        cur.type_code = conv.type_code
        cur.params = conv.params
      }
      const quoteField = (['kt1', 'kt2', 'kt3', 'kt4', 'dinh_luong'] as const).find((f) => f === field)
      const typed = quoteField ? String((cur as Record<string, unknown>)[quoteField] ?? '').trim() : ''
      const exact = quoteField && typed !== '' && foamQuoteSuggestions(String(cur.item_name ?? ''), quoteField, foamQuoteCatalog, cur as never)
        .some((option) => option.replace(/\s+/g, '').toLocaleLowerCase('vi') === typed.replace(/\s+/g, '').toLocaleLowerCase('vi'))
      // Đang gõ dở (chưa khớp lựa chọn nào) hoặc đang xóa trống: chỉ ghi giá trị, không tự đổi các ô khác.
      updated[index] = quoteField && !exact ? cur : autoFillSingleFoamQuoteFields(quoteField ? pruneLaterFoamQuoteFields(cur, quoteField, foamQuoteCatalog) : cur, foamQuoteCatalog)
      return updated
    })
  }

  function handleStandardFoamPaste(tsv: string, rowIndex: number, field: keyof FoamBomLine) {
    if (!isEditMode) return
    const fields: (keyof FoamBomLine)[] = ['item_name', 'kt1', 'kt2', 'kt3', 'kt4', 'dinh_luong', 'dvt', 'sl', 'ghi_chu']
    const start = Math.max(0, fields.indexOf(field))
    const rows = parseTsv(tsv)
    updateLines((prev) => {
      const next = [...prev]
      while (next.length < rowIndex + rows.length) next.push(createBlankFoamBomLine())
      rows.forEach((cells, offset) => {
        const row = { ...next[rowIndex + offset] }
        cells.forEach((cell, col) => {
          const target = fields[start + col]
          if (!target || target === 'dvt') return
          ;(row as Record<string, unknown>)[target] = parseFoamCell(target, cell)
        })
        row.dvt = 'Pcs'
        next[rowIndex + offset] = row
      })
      return next
    })
  }

  function handleContainerPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    if (!isEditMode) return
    const targetTag = (e.target as HTMLElement).tagName
    if (targetTag === 'INPUT' || targetTag === 'TEXTAREA' || targetTag === 'SELECT') {
      return
    }
    const text = e.clipboardData.getData('text/plain')
    if (text.includes('\t') || text.includes('\n')) {
      e.preventDefault()
      const targetRow = focusedCellRef.current?.rowIndex ?? (lines.length > 0 ? lines.length - 1 : 0)
      const targetField = focusedCellRef.current?.field ?? 'item_name'
      handleStandardFoamPaste(text, targetRow, targetField)
    }
  }

  // Excel Export
  function handleExportExcel() {
    exportFoamBomToExcel(
      productCode,
      getFoamAssemblyLabel(config.assembly_type),
      getFoamPackagingLabel(config.packaging_type),
      lines,
    )
    showToast('✓ Đã xuất file Excel!')
  }

  // Excel Import
  function handleImportExcel(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !isEditMode) return
    try {
      assertWorkbookFileSize(file.size)
    } catch (err: any) {
      showToast(`❌ Lỗi đọc file Excel: ${err.message}`)
      return
    }

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const buffer = evt.target?.result as ArrayBuffer
        validateWorkbookBytes(file.name, new Uint8Array(buffer))
        const imported = parseFoamBomExcel(buffer)
        if (imported.length === 0) {
          showToast('⚠️ Không tìm thấy dòng dữ liệu hợp lệ trong file Excel!')
          return
        }
        updateLines(imported)
        showToast(`✓ Đã nạp thành công ${imported.length} dòng từ file Excel!`)
      } catch (err: any) {
        showToast(`❌ Lỗi đọc file Excel: ${err.message || 'File không hợp lệ'}`)
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // Print PDF (A4 Portrait)
  function triggerPrint() {
    const existingStyle = document.getElementById('foam-bom-print-style')
    if (existingStyle) existingStyle.remove()

    const style = document.createElement('style')
    style.id = 'foam-bom-print-style'
    style.innerHTML = `@page { size: 210mm 297mm; margin: 8mm; }`
    document.head.appendChild(style)

    flushSync(() => {
      // Force render flush
    })

    setTimeout(() => {
      window.print()
      style.remove()
    }, 150)
  }

  // Dynamic combobox options for Định lượng
  const dynamicDinhLuongOptions = useMemo(() => {
    const set = new Set<string>(DEFAULT_DINH_LUONG_OPTIONS)
    lines.forEach((l) => {
      if (l.dinh_luong !== null && l.dinh_luong !== undefined && l.dinh_luong !== '') {
        set.add(formatDensityDisplay(l.dinh_luong))
      }
    })
    return Array.from(set)
  }, [lines])

  // Total quantity
  const totalQty = useMemo(
    () => lines.reduce((acc, curr) => acc + (curr.sl || 0), 0),
    [lines],
  )


  // Total material cost before VAT (from server authoritative results)
  const totalMaterialCost = useMemo(() => {
    return lines.reduce((acc, line, idx) => {
      const key = line.id || String(idx)
      const res = resolvedPrices[key]
      return acc + (res?.lineTotal || 0)
    }, 0)
  }, [lines, resolvedPrices])

  // Total VAT amount synchronized with supplier's default_vat_rate (fallback 8%)
  const totalVatAmount = useMemo(() => {
    return lines.reduce((acc, line, idx) => {
      const key = line.id || String(idx)
      const res = resolvedPrices[key]
      const lineCost = res?.lineTotal || 0
      if (lineCost <= 0) return acc
      const s = line.ncc_supplier_id ? supplierMap.get(line.ncc_supplier_id) : null
      const vatRate =
        res?.vatRateValue != null
          ? res.vatRateValue
          : (s?.default_vat_rate !== undefined && s?.default_vat_rate !== null && !isNaN(Number(s.default_vat_rate))
            ? Number(s.default_vat_rate)
            : 8)
      return acc + (lineCost * (vatRate / 100))
    }, 0)
  }, [lines, resolvedPrices, supplierMap])

  // Grand total packaging cost including VAT
  const grandTotalCostWithVat = useMemo(() => {
    return totalMaterialCost + totalVatAmount
  }, [totalMaterialCost, totalVatAmount])

  // Effective VAT rate for display (e.g. 8)
  const effectiveVatRate = useMemo(() => {
    if (totalMaterialCost <= 0) return 8
    const rawRate = (totalVatAmount / totalMaterialCost) * 100
    return Math.round(rawRate * 10) / 10
  }, [totalMaterialCost, totalVatAmount])



  return (
    <div className="foam-bom-root min-h-screen bg-slate-100 font-sans text-slate-800">
      <SafeBlocker when={hasUnsavedChanges} />

      <style>{`
        /* Bảng dạng Excel: ô vuông, viền đen, các ô sát nhau, ít màu. */
        .foam-bom-table { border-collapse: collapse !important; border: 1px solid #000; background: #fff; }
        .foam-bom-table th,
        .foam-bom-table td { border: 1px solid #000 !important; padding: 0 !important; vertical-align: middle; background: #fff; }
        .foam-bom-table th { background: #f2f2f2 !important; color: #000 !important; font-weight: 700; padding: 5px 6px !important; }
        .foam-bom-table * { border-radius: 0 !important; box-shadow: none !important; }
        .foam-bom-table td > * { margin: 0; }
        .foam-bom-table td input,
        .foam-bom-table td select { width: 100%; min-height: 28px; border: 0 !important; outline: none; background: #fff; padding: 3px 6px; }
        .foam-bom-table td input:focus,
        .foam-bom-table td select:focus { outline: 2px solid #1f6feb; outline-offset: -2px; }
        .foam-bom-table td input:disabled,
        .foam-bom-table td input[disabled] { background: #f2f2f2; }
        .foam-bom-table td { padding: 0 !important; white-space: normal; overflow-wrap: anywhere; font-size: 12px; line-height: 1.35; }
        .foam-bom-table th { white-space: normal; overflow-wrap: anywhere; font-size: 12px; }
        .foam-bom-table td input,
        .foam-bom-table td select { font-size: 12px; text-overflow: clip; }
        .foam-bom-table { table-layout: fixed; width: 100%; }
        .foam-bom-table input, .foam-bom-table select, .foam-bom-table button { box-sizing: border-box; max-width: 100%; min-width: 0; }
        .foam-bom-table td > div, .foam-bom-table td > span { max-width: 100%; }
        .foam-bom-table td { overflow: hidden; }
        .foam-bom-table td svg { flex-shrink: 1; max-width: 14px; }
        .foam-bom-table td, .foam-bom-table th { overflow-wrap: anywhere; word-break: break-word; }
        .foam-bom-table .truncate { overflow: visible; text-overflow: clip; white-space: normal; }
        .foam-bom-table tbody tr:hover td { background: #fff8dc; }
        .foam-bom-table [class*="bg-"]:not(input):not(select) { background-color: transparent !important; }
        .foam-bom-table tbody tr { border: 0 !important; }
        @page { size: 210mm 297mm; margin: 8mm; }
        @media print {
          body {
            background: #fff !important;
            color: #000 !important;
            font-family: 'Inter', sans-serif !important;
          }
          .foam-bom-root {
            background: #fff !important;
            padding: 0 !important;
          }
          * {
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .foam-bom-root section,
          .foam-bom-root div {
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .foam-info-table,
          .foam-info-table th,
          .foam-info-table td,
          .foam-spec-table,
          .foam-spec-table th,
          .foam-spec-table td,
          .foam-bom-table,
          .foam-bom-table th,
          .foam-bom-table td {
            border: 1px solid #000 !important;
            border-collapse: collapse !important;
            border-radius: 0 !important;
          }
          .foam-info-table {
            table-layout: auto !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          .foam-info-table th,
          .foam-info-table td {
            padding: 3px 6px !important;
            font-size: 11px !important;
            line-height: 1.35 !important;
          }
          .foam-spec-table {
            table-layout: auto !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          .foam-spec-table th,
          .foam-spec-table td {
            white-space: nowrap !important;
            word-break: normal !important;
            overflow-wrap: normal !important;
            font-size: 10.5px !important;
            padding: 3px 6px !important;
          }
          .foam-bom-table {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          .foam-bom-table thead {
            display: table-header-group !important;
          }
          .foam-bom-table tr {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .foam-bom-table th,
          .foam-bom-table td {
            border: 1px solid #000 !important;
            padding: 2.5px 3px !important;
            font-size: 10px !important;
            line-height: 1.3 !important;
            box-sizing: border-box !important;
            vertical-align: middle !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }
          /* Độ rộng cột chuẩn vừa khổ A4 dọc (tổng bề ngang in 194mm) */
          .foam-bom-table .col-stt { width: 7mm !important; }
          .foam-bom-table .col-name,
          .foam-bom-table .col-ten { width: 38mm !important; }
          .foam-bom-table .col-ncc { display: none !important; width: 0 !important; }
          .foam-bom-table .col-kt1 { width: 11mm !important; }
          .foam-bom-table .col-kt2 { width: 11mm !important; }
          .foam-bom-table .col-kt3 { width: 11mm !important; }
          .foam-bom-table .col-kt4 { width: 11mm !important; }
          .foam-bom-table .col-dl,
          .foam-bom-table .col-dinhluong,
          .foam-bom-table .col-loai { width: 14mm !important; }
          .foam-bom-table .col-uom,
          .foam-bom-table .col-dvt { width: 9mm !important; }
          .foam-bom-table .col-qty,
          .foam-bom-table .col-sl { width: 9mm !important; }
          .foam-bom-table .col-price,
          .foam-bom-table .col-dongia { width: 18mm !important; }
          .foam-bom-table .col-vat { width: 10mm !important; }
          .foam-bom-table .col-total,
          .foam-bom-table .col-thanhtien { width: 21mm !important; }
          .foam-bom-table .col-note,
          .foam-bom-table .col-ghichu { width: 24mm !important; }
          .foam-bom-table .col-act,
          .foam-bom-table .col-action { display: none !important; width: 0 !important; }

          /* Cột ngắn: chữ số/ĐVT/STT/VAT/SL KHÔNG bao giờ bị bẻ */
          .foam-bom-table .col-stt,
          .foam-bom-table .col-kt1,
          .foam-bom-table .col-kt2,
          .foam-bom-table .col-kt3,
          .foam-bom-table .col-kt4,
          .foam-bom-table .col-dl,
          .foam-bom-table .col-dinhluong,
          .foam-bom-table .col-loai,
          .foam-bom-table .col-uom,
          .foam-bom-table .col-dvt,
          .foam-bom-table .col-qty,
          .foam-bom-table .col-sl,
          .foam-bom-table .col-price,
          .foam-bom-table .col-dongia,
          .foam-bom-table .col-vat,
          .foam-bom-table .col-total,
          .foam-bom-table .col-thanhtien,
          .foam-bom-table .col-stt *,
          .foam-bom-table .col-kt1 *,
          .foam-bom-table .col-kt2 *,
          .foam-bom-table .col-kt3 *,
          .foam-bom-table .col-kt4 *,
          .foam-bom-table .col-dl *,
          .foam-bom-table .col-dinhluong *,
          .foam-bom-table .col-loai *,
          .foam-bom-table .col-uom *,
          .foam-bom-table .col-dvt *,
          .foam-bom-table .col-qty *,
          .foam-bom-table .col-sl *,
          .foam-bom-table .col-price *:not(.price-status-text),
          .foam-bom-table .col-dongia *:not(.price-status-text),
          .foam-bom-table .col-vat *,
          .foam-bom-table .col-total *,
          .foam-bom-table .col-thanhtien * {
            white-space: nowrap !important;
            word-break: normal !important;
            overflow-wrap: normal !important;
          }

          /* Ô cảnh báo/trạng thái giá: được xuống dòng tự nhiên, không bao giờ bị cắt chữ */
          .foam-bom-table .price-status-text,
          .foam-bom-table .col-price .price-status-text,
          .foam-bom-table .col-dongia .price-status-text {
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: anywhere !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }

          /* Cột dài: chỉ cột Tên hàng/chi tiết, Ghi chú được xuống dòng giữa các TỪ */
          .foam-bom-table .col-name,
          .foam-bom-table .col-ten,
          .foam-bom-table .col-note,
          .foam-bom-table .col-ghichu,
          .foam-bom-table .bom-wrap-text {
            white-space: normal !important;
            word-break: normal !important;
            overflow-wrap: break-word !important;
          }

          .foam-bom-table input,
          .foam-bom-table select,
          .foam-bom-table textarea {
            display: none !important;
          }

          .foam-bom-table .print-val {
            display: inline !important;
          }

          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* ---------------- SPREADSHEET HEADER BAR ---------------- */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-700 bg-slate-900 px-4 py-2.5 text-white shadow-md print:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-slate-700 active:scale-95"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Quay lại
          </button>
          <div>
            <h1 className="text-sm font-bold text-white flex items-center gap-2">
              <span className="rounded bg-blue-600 px-1.5 py-0.5 text-[11px] font-mono font-semibold">
                BOM ĐÓNG GÓI
              </span>
              <span>
                {productCode} — {productName}
              </span>
            </h1>
            <p className="text-[11px] text-slate-400">
              Kết cấu: <span className="text-blue-300 font-semibold">{getFoamAssemblyLabel(config.assembly_type)}</span> | Đóng gói: <span className="text-amber-300 font-semibold">{getFoamPackagingLabel(config.packaging_type)}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Toast Notification */}
          {toastMsg && (
            <div className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm animate-fade-in">
              {toastMsg}
            </div>
          )}

          {/* Toggle Edit / Save Button */}
          <button
            type="button"
            onClick={handleToggleEdit}
            className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-bold transition shadow-xs active:scale-95 ${
              isEditMode
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500'
                : 'bg-blue-600 hover:bg-blue-700 text-white border border-blue-500'
            }`}
          >
            {isEditMode ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {isEditMode ? <><Save size={15} aria-hidden="true" /> Lưu</> : <><Pencil size={15} aria-hidden="true" /> Chỉnh sửa</>}
          </button>

          {/* Cancel Edit Button */}
          {isEditMode && (
            <button
              type="button"
              onClick={handleCancelEdit}
              className="inline-flex items-center gap-1 rounded bg-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-600 border border-slate-600"
            >
              <RotateCcw className="h-3 w-3" />
              Hủy
            </button>
          )}

          {/* Import Excel Button - CHỈ hiển thị khi isEditMode */}
          {isEditMode && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-800 border border-emerald-600 shadow-xs"
                title="Nạp file Excel ghi đè bảng hiện tại"
              >
                <Upload className="h-3.5 w-3.5" />
                Nạp Excel
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
                className="hidden"
              />
            </>
          )}

          {/* Export Excel Button */}
          <button
            type="button"
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 rounded bg-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-600 border border-slate-600"
            title="Xuất dữ liệu bảng tính ra file Excel (.xlsx)"
          >
            <Download className="h-3.5 w-3.5" />
            Xuất Excel
          </button>

          {/* Print Button */}
          <button
            type="button"
            onClick={triggerPrint}
            className="inline-flex items-center gap-1.5 rounded bg-red-700 px-3 py-1.5 text-xs font-bold text-white border border-red-600 hover:bg-red-800 shadow-xs"
            title="In hoặc xuất PDF khổ A4 Đứng"
          >
            <Printer className="h-3.5 w-3.5" />
            In / PDF
          </button>

          {/* Revisions History Button */}
          <button
            type="button"
            onClick={() => setShowHistoryModal(true)}
            className="inline-flex items-center gap-1 rounded bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-700 border border-slate-700"
            title="Xem lịch sử các phiên bản BOM"
          >
            <History className="h-3.5 w-3.5" />
            Lịch sử ({revisions.length})
          </button>
        </div>
      </header>

      {/* Pricing error alert */}
      {pricingError && (
        <div className="mx-4 mt-3 bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2.5 rounded-lg text-xs flex items-center justify-between no-print shadow-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold">⚠️ Lỗi tính giá từ máy chủ:</span>
            <span>{pricingError}</span>
          </div>
          <button
            type="button"
            onClick={() => setPricingError(null)}
            className="text-rose-600 hover:text-rose-800 text-xs font-semibold px-2 py-0.5 rounded cursor-pointer"
          >
            Đóng
          </button>
        </div>
      )}

      {/* ---------------- MAIN CONTAINER ---------------- */}
      <main className="w-full px-4 sm:px-6 py-4 print:p-0 print:max-w-none">
        {/* Specification Header Card (Printed as header block) */}
        <div className="mb-4 rounded-none border border-slate-300 bg-white p-4 shadow-none print:border-black print:border print:rounded-none print:shadow-none print:p-2 print:mb-2">
          <div className="border-b border-slate-200 pb-3 mb-3 print:pb-2 print:mb-2 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide">
                BẢNG ĐỊNH MỨC VẬT TƯ ĐÓNG GÓI (MÚT XỐP / CARTON)
              </h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                MỘC PHÁT FURNITURE ERP — TIÊU CHUẨN ĐÓNG GÓI SẢN PHẨM XUẤT KHẨU
              </p>
            </div>
            <div className="text-right text-xs">
              <span className="font-bold text-slate-900">{currentRev?.revLabel || 'Rev. 01'}</span>
              <span className="text-slate-500 ml-2">({currentRev?.savedAt || formatDate(new Date())})</span>
            </div>
          </div>

          <div className="flex items-stretch justify-between p-1 gap-3 bg-white">
            <div className="flex-1 overflow-x-auto">
              <table className="product-info-table foam-info-table w-full text-left text-xs border-collapse border border-black">
                <tbody>
                  <tr className="border-b border-slate-200 print:border-black">
                    <td className="w-24 bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      MÃ HÀNG (SKU)
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 print:border-black font-mono text-sm font-black text-slate-900 print:text-black">
                      {productCode}
                    </td>
                    <td className="w-28 bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      TÊN SẢN PHẨM
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 print:border-black font-bold text-slate-900 print:text-black">
                      {productName}
                    </td>
                    <td className="w-24 bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      KHÁCH HÀNG
                    </td>
                    <td className="px-2.5 py-1.5 font-semibold text-slate-800 print:text-black">
                      {customer}
                    </td>
                  </tr>

                  <tr className="border-b border-slate-200 print:border-black">
                    <td className="bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      KẾT CẤU
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 print:border-black font-bold text-blue-700 print:text-black">
                      {getFoamAssemblyLabel(config.assembly_type)}
                    </td>
                    <td className="bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      HÌNH THỨC ĐÓNG GÓI
                    </td>
                    <td className="px-2.5 py-1.5 border-r border-slate-200 print:border-black font-bold text-amber-700 print:text-black">
                      {getFoamPackagingLabel(config.packaging_type)}
                    </td>
                    <td className="bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      SỐ LOẠI / TỔNG SL
                    </td>
                    <td className="px-2.5 py-1.5 font-bold text-slate-900 print:text-black">
                      {lines.length} loại / {totalQty} chi tiết
                    </td>
                  </tr>

                  <tr>
                    <td className="bg-slate-50 print:bg-white px-2.5 py-1.5 font-bold text-slate-600 print:text-black uppercase border-r border-slate-200 print:border-black whitespace-nowrap">
                      Tổng chi phí đóng gói:
                    </td>
                    <td colSpan={5} className="px-2.5 py-1.5 font-bold text-blue-700 print:text-black font-mono">
                      {grandTotalCostWithVat > 0 ? `${Math.round(grandTotalCostWithVat).toLocaleString("vi-VN")} đ` : "—"}
                      {grandTotalCostWithVat > 0 && (
                        <span className="text-[11px] text-slate-600 print:text-black font-mono ml-2 font-normal">
                          (Vật tư: {Math.round(totalMaterialCost).toLocaleString("vi-VN")} đ + VAT {effectiveVatRate}%: {Math.round(totalVatAmount).toLocaleString("vi-VN")} đ)
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Khối Ảnh Sản Phẩm (~35mm, viền đen đặc, không bo góc) */}
            <div
              className="product-photo-box w-[35mm] h-[35mm] min-w-[35mm] min-h-[35mm] max-w-[35mm] max-h-[35mm] border border-black rounded-none flex items-center justify-center p-0.5 bg-white overflow-hidden text-center shrink-0 self-center"
              style={{ width: '35mm', height: '35mm', border: '1px solid #000', borderRadius: '0' }}
            >
              {productImageUrl ? (
                <img
                  src={productImageUrl}
                  alt={productName}
                  className="w-full h-full object-contain"
                  style={{ borderRadius: '0' }}
                />
              ) : null}
            </div>
          </div>

          {/* Thông Tin Đóng Gói (Cấp cấu hình) - Hỗ trợ đến 3 thùng */}
          <div className="mt-4 pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
                <Package className="h-4 w-4 text-blue-600" />
                Quy Cách Phủ Bì Thùng & Trọng Lượng (Tối đa 3 thùng)
              </span>
              {!isEditMode && (
                <span className="text-[11px] text-slate-400 italic">
                  (Bấm "✏️ Chỉnh sửa" để điền kích thước phủ bì & trọng lượng cho từng thùng)
                </span>
              )}
            </div>

            <div className="overflow-x-auto rounded-none border border-black bg-slate-50/40 print:bg-white print:border-black print:rounded-none">
              <table className="foam-spec-table w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 border-b border-slate-200 text-slate-700 font-bold text-[11px] uppercase tracking-wider">
                    <th className="px-3 py-2 text-center w-28">Thùng / Kiện</th>
                    <th className="px-3 py-2 min-w-[320px]">Kích thước Phủ Bì (Dài × Rộng × Cao)</th>
                    <th className="px-3 py-2 w-48 text-center">Net Weight (NW)</th>
                    <th className="px-3 py-2 w-48 text-center">Gross Weight (GW)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/80 bg-white">
                  {/* THÙNG 1 */}
                  <tr className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-center font-bold text-blue-700 bg-slate-50/60">
                      Thùng 1
                    </td>
                    <td className="px-3 py-2">
                      {isEditMode ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={currentConfig.phu_bi_dai ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_dai: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Dài"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_rong ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_rong: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Rộng"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_cao ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_cao: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Cao"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px] ml-1">mm</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.phu_bi_dai || currentConfig.phu_bi_rong || currentConfig.phu_bi_cao
                            ? `${currentConfig.phu_bi_dai ?? '—'} × ${currentConfig.phu_bi_rong ?? '—'} × ${currentConfig.phu_bi_cao ?? '—'} mm`
                            : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.net_weight ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                net_weight: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.net_weight != null ? `${currentConfig.net_weight} kg` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.gross_weight ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                gross_weight: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.gross_weight != null ? `${currentConfig.gross_weight} kg` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* THÙNG 2 */}
                  <tr className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-center font-bold text-blue-700 bg-slate-50/60">
                      Thùng 2
                    </td>
                    <td className="px-3 py-2">
                      {isEditMode ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={currentConfig.phu_bi_2_dai ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_2_dai: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Dài"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_2_rong ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_2_rong: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Rộng"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_2_cao ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_2_cao: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Cao"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px] ml-1">mm</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.phu_bi_2_dai || currentConfig.phu_bi_2_rong || currentConfig.phu_bi_2_cao
                            ? `${currentConfig.phu_bi_2_dai ?? '—'} × ${currentConfig.phu_bi_2_rong ?? '—'} × ${currentConfig.phu_bi_2_cao ?? '—'} mm`
                            : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.net_weight_2 ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                net_weight_2: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.net_weight_2 != null ? `${currentConfig.net_weight_2} kg` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.gross_weight_2 ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                gross_weight_2: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.gross_weight_2 != null ? `${currentConfig.gross_weight_2} kg` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>

                  {/* THÙNG 3 */}
                  <tr className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-center font-bold text-blue-700 bg-slate-50/60">
                      Thùng 3
                    </td>
                    <td className="px-3 py-2">
                      {isEditMode ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={currentConfig.phu_bi_3_dai ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_3_dai: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Dài"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_3_rong ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_3_rong: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Rộng"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-400 font-bold">×</span>
                          <input
                            type="number"
                            value={currentConfig.phu_bi_3_cao ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                phu_bi_3_cao: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="Cao"
                            className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-center font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px] ml-1">mm</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.phu_bi_3_dai || currentConfig.phu_bi_3_rong || currentConfig.phu_bi_3_cao
                            ? `${currentConfig.phu_bi_3_dai ?? '—'} × ${currentConfig.phu_bi_3_rong ?? '—'} × ${currentConfig.phu_bi_3_cao ?? '—'} mm`
                            : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.net_weight_3 ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                net_weight_3: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.net_weight_3 != null ? `${currentConfig.net_weight_3} kg` : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isEditMode ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <input
                            type="number"
                            step="0.01"
                            value={currentConfig.gross_weight_3 ?? ''}
                            onChange={(e) =>
                              setCurrentConfig((prev) => ({
                                ...prev,
                                gross_weight_3: e.target.value === '' ? null : Number(e.target.value),
                              }))
                            }
                            placeholder="0.00"
                            className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-right font-mono text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-slate-500 font-semibold text-[11px]">kg</span>
                        </div>
                      ) : (
                        <span className="font-mono font-semibold text-slate-900">
                          {currentConfig.gross_weight_3 != null ? `${currentConfig.gross_weight_3} kg` : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ---------------- SPREADSHEET TABLE ---------------- */}
        <div className="rounded-none border border-slate-300 bg-white shadow-none overflow-hidden print:border-none print:shadow-none print:rounded-none">
          {lines.length === 0 ? (
            <div className="py-12 px-4">
              <EmptyState
                title="Chưa có dữ liệu định mức đóng gói"
                description={
                  isEditMode
                    ? 'Bấm nút "Thêm dòng" bên dưới để bắt đầu thêm vật tư bao bì, mút xốp.'
                    : 'Bấm nút "Sửa" ở thanh công cụ phía trên hoặc nút bên dưới để bắt đầu nhập dữ liệu định mức đóng gói.'
                }
                action={
                  <button
                    type="button"
                    onClick={handleAddRow}
                    className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 active:scale-95 cursor-pointer"
                  >
                    <Plus className="h-4 w-4" />
                    Thêm dòng
                  </button>
                }
              />
            </div>
          ) : (
            <div
              className="overflow-x-auto outline-none"
              tabIndex={0}
              onPaste={handleContainerPaste}
            >
              <table className="foam-bom-table w-full text-left text-xs border-collapse">
  <colgroup>
    {visibleCols.map((c) => (
      <col
        key={c.key}
        className={`col-${c.key}${c.key === 'act' || c.key === 'ncc' ? ' no-print' : ''}`}
        style={{ width: `${(colWidths[c.key] / totalWeight) * 100}%` }}
      />
    ))}
  </colgroup>
  <thead>
    <tr className="border-b border-slate-300 bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-700">
      {visibleCols.map((c, ci) => (
        <th
          key={c.key}
          className={`relative px-1 py-2 ${c.align} col-${c.key}${c.key === 'act' || c.key === 'ncc' ? ' no-print' : ''}`}
          title={c.title}
          aria-label={c.key === 'act' ? 'Thao tác dòng' : undefined}
        >
          {c.label}
          {ci < visibleCols.length - 1 ? (
            <span
              role="separator"
              aria-label={`Kéo để chỉnh độ rộng cột ${c.label}`}
              className="no-print absolute right-0 top-0 h-full w-1.5 cursor-col-resize select-none hover:bg-blue-400"
              onMouseDown={(event) => startColumnResize(event, ci)}
              onDoubleClick={() => setColWidths((prev) => ({ ...prev, [c.key]: DEFAULT_BOM_COLS[c.key] }))}
            />
          ) : null}
        </th>
      ))}
    </tr>
  </thead>
  <tbody>
    {lines.map((row, idx) => {
      const displayName = (row.loai?.trim() || row.item_name?.trim() || foamBomPriceKind(row) || '')
      const itemName = (row.item_name?.trim() || row.loai?.trim() || foamBomPriceKind(row) || '')
      const semantics = getFoamQuoteSemantics(itemName)
      const resolved = resolvedPrices[row.id || String(idx)]
      const supplier = row.ncc_supplier_id ? supplierMap.get(row.ncc_supplier_id) : null
      const vatRate = resolved?.vatRateValue ?? (supplier?.default_vat_rate || 8)
      const lineTotalAfterVat = resolved?.lineTotalWithVat ?? (resolved?.lineTotal == null ? null : resolved.lineTotal * (1 + vatRate / 100))
      const money = (value: number | null | undefined) => value == null ? '—' : `${Math.round(value).toLocaleString('vi-VN')} đ`
      const autocompleteSuggestions = buildFoamBomAutocompleteSuggestions({
        query: itemName,
        quoteRules: foamQuoteCatalog,
        materials: [],
        standardNames: [],
        usedNames: usedPackagingNames,
      })
      const renderField = (field: FoamQuoteField, columnIndex: number) => {
        const disabled = !isEditMode || !itemName || !foamQuoteFieldIsUsed(itemName, field, foamQuoteCatalog)
        const suggestions = foamQuoteSuggestions(itemName, field, foamQuoteCatalog, row)
        const value = row[field] ?? ''
        const listId = 'foam-quote-' + productId + '-' + idx + '-' + field
        return (
          <td key={field} className={`px-1.5 py-1.5 text-center col-${field}`}>
            <span className="hidden print:inline font-mono text-xs font-semibold print-val">
              {value === '' || value == null ? '—' : String(value)}
            </span>
            {isEditMode ? (
              <div className="print:hidden">
                <input
                  type={field === 'kt4' ? 'text' : 'number'}
                  step="any"
                  value={String(value)}
                  disabled={disabled}
                  title={semantics[columnIndex]}
                  aria-label={'Dòng ' + (idx + 1) + ' KT' + (columnIndex + 1) + ' ' + semantics[columnIndex]}
                  list={listId}
                  onFocus={() => { focusedCellRef.current = { rowIndex: idx, field } }}
                  onPaste={(event) => {
                    const pasted = event.clipboardData.getData('text/plain')
                    if (pasted.includes('\t') || pasted.includes('\n')) {
                      event.preventDefault()
                      handleStandardFoamPaste(pasted, idx, field)
                    }
                  }}
                  onChange={(event) => {
                    const raw = event.target.value
                    handleCellChange(idx, field, raw === '' ? null : field === 'kt4' ? raw : Number(raw))
                  }}
                  className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-center font-mono text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                />
                <datalist id={listId}>{suggestions.map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>
              </div>
            ) : (
              <span className="font-mono text-xs print:hidden" title={semantics[columnIndex]}>{value === '' || value == null ? '—' : String(value)}</span>
            )}
          </td>
        )
      }
      return (
        <tr key={row.id || idx} className="hover:bg-slate-50/60 transition-colors">
          <td className="px-2 py-2 text-center font-mono font-semibold text-slate-500 col-stt">{idx + 1}</td>
          <td className="px-2 py-1.5 col-name col-ten">
            <span className="hidden print:inline text-xs font-semibold text-slate-900 print-val">
              {displayName || '—'}
            </span>
            {isEditMode ? (
              <div className="print:hidden">
                <Typeahead<FoamBomAutocompleteSuggestion>
                  items={autocompleteSuggestions}
                  value={null}
                  inputValue={itemName}
                  allowCustomValue
                  displayValue={(suggestion) => suggestion.name}
                  getItemKey={(suggestion) => suggestion.id}
                  onInputChange={(value) => handleCellChange(idx, 'item_name', value)}
                  onPaste={(event) => {
                    const pasted = event.clipboardData.getData('text/plain')
                    if (pasted.includes('\t') || pasted.includes('\n')) {
                      event.preventDefault()
                      handleStandardFoamPaste(pasted, idx, 'item_name')
                    }
                  }}
                  onChange={(suggestion) => {
                    if (!suggestion) return
                    updateLines((prev) => {
                      const next = [...prev]
                      next[idx] = autoFillSingleFoamQuoteFields(applyFoamBomAutocompleteSuggestion(next[idx], suggestion), foamQuoteCatalog)
                      return next
                    })
                  }}
                  renderItem={(suggestion) => (
                    <div className="flex items-center justify-between gap-3">
                      <span>{suggestion.name}</span>
                      <span className="shrink-0 text-[10px] text-slate-400">
                        {suggestion.source === 'quote' ? `NCC${suggestion.supplierName ? ` · ${suggestion.supplierName}` : ''}` : suggestion.source === 'material' ? 'Vật tư' : 'BOM'}
                      </span>
                    </div>
                  )}
                  onFocus={() => { focusedCellRef.current = { rowIndex: idx, field: 'item_name' } }}
                  aria-label={'Dòng ' + (idx + 1) + ' Tên hàng'}
                  inputClassName="px-2 py-1 text-xs font-semibold"
                  className="w-full"
                />
              </div>
            ) : (
              <span className="text-xs font-semibold text-slate-900 print:hidden" title={displayName}>{displayName || '—'}</span>
            )}
          </td>
          {showSupplierColumn ? (
            <td className="px-2 py-1.5 text-xs col-ncc no-print">
              <div className="font-semibold text-slate-800">{supplier?.name ?? '—'}</div>
              {resolved?.priceSource === 'SUPPLIER_QUOTE' ? <span className="text-[10px] font-semibold text-emerald-700">NCC ✓</span> : null}
              {resolved?.status === 'NEEDS_MAPPING' ? <span className="text-[10px] font-semibold text-amber-700">NEEDS_MAPPING</span> : null}
              {resolved?.status === 'NO_QUOTE' ? <span className="text-[10px] font-semibold text-amber-700">NO_QUOTE</span> : null}
              {resolved?.reason ? <div className="text-[10px] text-slate-500" title={resolved.reason}>{resolved.reason}</div> : null}
              {resolved?.vatRateDefaulted ? <div className="text-[10px] text-slate-500">VAT mặc định 8%</div> : null}
            </td>
          ) : null}
          {(['kt1', 'kt2', 'kt3', 'kt4'] as FoamQuoteField[]).map(renderField)}
          <td className="px-1.5 py-1.5 text-center col-dl col-dinhluong col-loai">
            <span className="hidden print:inline font-mono text-xs font-semibold print-val">
              {row.dinh_luong || '—'}
            </span>
            {isEditMode ? (
              <div className="print:hidden">
                <input
                  type="text"
                  list={'foam-quote-' + productId + '-' + idx + '-dinh_luong'}
                  value={row.dinh_luong ?? ''}
                  disabled={!itemName || !foamQuoteFieldIsUsed(itemName, 'dinh_luong', foamQuoteCatalog)}
                  title={semantics[4]}
                  aria-label={'Dòng ' + (idx + 1) + ' Định lượng'}
                  onFocus={() => { focusedCellRef.current = { rowIndex: idx, field: 'dinh_luong' } }}
                  onChange={(event) => handleCellChange(idx, 'dinh_luong', event.target.value || null)}
                  className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-center font-mono text-xs text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                />
                <datalist id={'foam-quote-' + productId + '-' + idx + '-dinh_luong'}>{foamQuoteSuggestions(itemName, 'dinh_luong', foamQuoteCatalog, row).map((value) => <option key={value} value={value} />)}</datalist>
              </div>
            ) : <span className="font-mono text-xs print:hidden">{row.dinh_luong ?? '—'}</span>}
          </td>
          <td className="px-1 py-1.5 text-center text-xs col-uom col-dvt">
            <span className="print-val">Pcs</span>
          </td>
          <td className="px-1.5 py-1.5 text-center col-qty col-sl">
            <span className="hidden print:inline font-mono text-xs font-bold text-slate-900 print-val">
              {row.sl ?? 1}
            </span>
            <input
              type="number"
              min="1"
              value={row.sl}
              disabled={!isEditMode}
              aria-label={'Dòng ' + (idx + 1) + ' SL'}
              onFocus={() => { focusedCellRef.current = { rowIndex: idx, field: 'sl' } }}
              onChange={(event) => handleCellChange(idx, 'sl', event.target.value === '' ? 1 : Math.max(1, Number(event.target.value)))}
              className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-center font-mono text-xs font-bold text-emerald-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:border-transparent disabled:bg-transparent print:hidden"
            />
          </td>
          <td className={`px-2 py-1.5 text-right font-mono text-xs col-price col-dongia ${pricingPending ? 'opacity-40' : ''}`} title={resolved?.unitPrice == null ? resolved?.reason ?? undefined : undefined}>
            <span className={`print-val ${resolved?.unitPrice == null && resolved?.reason ? 'bom-wrap-text' : 'whitespace-nowrap'}`}>
              {resolved?.unitPrice == null && resolved?.reason ? (
                <span className="price-status-text block text-left font-sans text-[11px] text-slate-600 print:text-[10px] leading-tight">
                  {formatPricingWarningText(resolved.reason)}
                </span>
              ) : (
                money(resolved?.unitPrice)
              )}
            </span>
          </td>
          <td className={`px-2 py-1.5 text-right font-mono text-xs col-vat ${pricingPending ? 'opacity-40' : ''}`}>
            <span className="whitespace-nowrap print-val">{resolved?.unitPrice == null ? '—' : `${vatRate}%`}</span>
          </td>
          <td className={`px-2 py-1.5 text-right font-mono text-xs font-semibold col-total col-thanhtien ${pricingPending ? 'opacity-40' : ''}`}>
            <span className="whitespace-nowrap print-val">{money(lineTotalAfterVat)}</span>
          </td>
          <td className="px-2 py-1.5 col-note col-ghichu bom-wrap-text">
            <span className="hidden print:inline text-xs text-slate-700 print-val">
              {row.ghi_chu || ''}
            </span>
            <input
              type="text"
              value={row.ghi_chu || ''}
              disabled={!isEditMode}
              aria-label={'Dòng ' + (idx + 1) + ' Ghi chú'}
              onFocus={() => { focusedCellRef.current = { rowIndex: idx, field: 'ghi_chu' } }}
              onChange={(event) => handleCellChange(idx, 'ghi_chu', event.target.value)}
              className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:border-transparent disabled:bg-transparent print:hidden"
            />
          </td>
          <td className="px-2 py-1.5 text-center no-print whitespace-nowrap col-act col-action">
            {isEditMode && <div className="flex items-center justify-center gap-0.5">
              <button type="button" onClick={() => handleMoveRowUp(idx)} disabled={idx === 0} className="rounded p-1 text-slate-500 disabled:opacity-30" title="Di chuyển lên" aria-label="Di chuyển lên"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => handleMoveRowDown(idx)} disabled={idx === lines.length - 1} className="rounded p-1 text-slate-500 disabled:opacity-30" title="Di chuyển xuống" aria-label="Di chuyển xuống"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => handleDeleteRow(idx)} className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Xóa dòng này" aria-label="Xóa dòng này"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>}
          </td>
        </tr>
      )
    })}
  </tbody>
</table>
          </div>
          )}

          {/* Datalists for Comboboxes */}
          <datalist id="foam-dinhluong-datalist">
            {dynamicDinhLuongOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt} kg/m³
              </option>
            ))}
          </datalist>

          <datalist id="foam-dvt-datalist">
            {DEFAULT_DVT_OPTIONS.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>

          {/* Add Row Button & Total Summary */}
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 flex flex-wrap justify-between items-center gap-2 print:bg-white print:border-slate-800 print:px-1 print:py-2.5 print:border-t-2">
            <div className="flex items-center gap-2 no-print">
              {isEditMode && (
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-400 active:scale-95 shadow-2xs cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5 text-blue-600" />
                  Thêm dòng vật tư đóng gói
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowSupplierColumn((v) => !v)}
                className="inline-flex items-center whitespace-nowrap rounded border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                {showSupplierColumn ? 'Ẩn cột NCC / nguồn giá' : 'Hiện cột NCC / nguồn giá'}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs print:w-full print:justify-end print:gap-3 print:text-xs">
              <span className="text-slate-500 print:text-slate-700 whitespace-nowrap">
                Tổng số dòng: <strong className="text-slate-700">{lines.length}</strong> | Tổng SL chi tiết: <strong className="text-slate-700">{totalQty}</strong>
              </span>
              <span className="rounded-none bg-slate-100 border border-slate-200 px-2.5 py-1 text-slate-700 font-medium print:bg-transparent print:border-black print:border print:rounded-none print:px-2 print:py-0.5 whitespace-nowrap">
                Tổng tiền vật tư: <strong className="font-mono font-bold text-slate-900">{totalMaterialCost > 0 ? Math.round(totalMaterialCost).toLocaleString('vi-VN') + ' đ' : '0 đ'}</strong>
              </span>
              <span className="rounded-none bg-amber-50 border border-amber-200 px-2.5 py-1 text-amber-800 font-medium print:bg-transparent print:border-black print:border print:rounded-none print:text-black print:px-2 print:py-0.5 whitespace-nowrap">
                VAT ({effectiveVatRate}%): <strong className="font-mono font-bold text-amber-900 print:text-slate-900">{totalVatAmount > 0 ? Math.round(totalVatAmount).toLocaleString('vi-VN') + ' đ' : '0 đ'}</strong>
              </span>
              <span className="rounded-none bg-blue-100 border border-blue-300 px-3 py-1 font-semibold text-blue-800 print:bg-transparent print:border-black print:border print:rounded-none print:text-black print:px-2 print:py-0.5 whitespace-nowrap">
                Tổng chi phí (gồm VAT): <strong className="font-mono font-bold text-blue-900 print:text-slate-900">{grandTotalCostWithVat > 0 ? Math.round(grandTotalCostWithVat).toLocaleString('vi-VN') + ' đ' : '0 đ'}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* ---------------- KHỐI CHỮ KÝ PHÊ DUYỆT TRÁCH NHIỆM ---------------- */}
        <section className="mt-8 hidden rounded-none border border-black bg-white p-4 shadow-none print:block print:shadow-none print:border-black print:rounded-none print:break-inside-avoid">
          <div className="mx-auto grid max-w-2xl grid-cols-2 gap-6">
            <div className="flex h-36 flex-col justify-between rounded-none border border-black bg-white p-3 text-center print:bg-white print:border-black print:rounded-none">
              <div>
                <div className="text-xs font-black uppercase text-slate-900">
                  KỸ THUẬT LẬP BIỂU & KIỂM TRA
                </div>
                <div className="text-[11px] italic text-slate-500">
                  (Tính toán bóc tách & rà soát định mức)
                </div>
              </div>
              <div className="border-t border-solid border-black pt-2 font-serif text-lg font-bold italic text-emerald-800 print:text-black print:border-black print:border-solid">
                Hà Phan Thanh Tâm
              </div>
            </div>

            <div className="flex h-36 flex-col justify-between rounded-none border border-black bg-white p-3 text-center print:bg-white print:border-black print:rounded-none">
              <div>
                <div className="text-xs font-black uppercase text-slate-900">
                  BAN GIÁM ĐỐC DUYỆT
                </div>
                <div className="text-[11px] italic text-slate-500">
                  (Phê duyệt chính thức lệnh sản xuất)
                </div>
              </div>
              <div className="border-t border-solid border-black pt-2 font-serif text-lg font-bold italic text-blue-900 print:text-black print:border-black print:border-solid">
                Nguyễn Thị Ngọc
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ---------------- REVISION HISTORY MODAL ---------------- */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 no-print">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
              <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <History className="h-4 w-4 text-blue-600" />
                Lịch sử phiên bản BOM Đóng Gói
              </h4>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {revisions.length === 0 ? (
                <p className="py-6 text-center text-xs text-slate-400">
                  Chưa có phiên bản nào được ghi nhận.
                </p>
              ) : (
                revisions.map((rev) => (
                  <div key={rev.id} className="py-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-blue-700">{rev.revLabel}</span>
                      <span className="text-slate-400 text-[11px]">
                        {rev.savedAt} {rev.savedTime}
                      </span>
                    </div>
                    <div className="text-slate-600 mt-0.5">
                      <span className="font-semibold text-slate-700">Tác giả:</span> {rev.author}
                    </div>
                    <div className="text-slate-500 text-[11px] mt-0.5">
                      <span className="font-semibold text-slate-700">Tóm tắt:</span> {rev.changesSummary}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
